import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Upload, Copy, Download, Save, AlertCircle, Check, RefreshCw, Globe, ChevronDown, X } from 'lucide-react';
import { User, Tab, ContractType, PostTone } from '../lib/types';
import { generateJobPost } from '../lib/chatbase';
import { decrementCredits, hasRemainingCredits, saveJobPost } from '../lib/auth';
import TextEditor from './TextEditor';

interface PimperPageProps {
  user: User;
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
}

const PimperPage: React.FC<PimperPageProps> = ({ user, activeTab, onTabChange }) => {
  const [originalText, setOriginalText] = useState('');
  const [pimpedText, setPimpedText] = useState('');
  const [editableText, setEditableText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [companyName, setCompanyName] = useState('');
  const [position, setPosition] = useState('');
  const [location, setLocation] = useState('');
  const [experience, setExperience] = useState('');
  const [skills, setSkills] = useState('');
  const [contractType, setContractType] = useState<ContractType>('CDI');
  const [tone, setTone] = useState<PostTone>('Formelle');
  const [jobLink, setJobLink] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyValues, setCompanyValues] = useState<string[]>([]);
  const [newValue, setNewValue] = useState('');
  
  const [isToneModalOpen, setIsToneModalOpen] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  
  const toneModalRef = useRef<HTMLDivElement>(null);
  const contractModalRef = useRef<HTMLDivElement>(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toneModalRef.current && !toneModalRef.current.contains(event.target as Node)) {
        setIsToneModalOpen(false);
      }
      if (contractModalRef.current && !contractModalRef.current.contains(event.target as Node)) {
        setIsContractModalOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddValue = () => {
    if (newValue.trim() && companyValues.length < 5) {
      setCompanyValues([...companyValues, newValue.trim()]);
      setNewValue('');
    }
  };
  
  const handleRemoveValue = (index: number) => {
    setCompanyValues(companyValues.filter((_, i) => i !== index));
  };

  const handlePimperOffre = async () => {
    if (!originalText.trim()) {
      setError('Veuillez entrer le texte de l\'offre à améliorer.');
      return;
    }

    if (!hasRemainingCredits()) {
      setError('Vous avez épuisé vos crédits. Veuillez passer à un forfait supérieur.');
      return;
    }

    setError(null);
    setSuccess(null);
    setIsProcessing(true);

    try {
      const pimped = await generateJobPost(
        companyName || 'Amélioration',
        position || 'Pimper cette offre',
        location,
        experience,
        skills,
        contractType,
        tone,
        jobLink,
        companyValues,
        companyWebsite,
        originalText
      );

      setPimpedText(pimped);
      setEditableText(pimped);
      setSuccess('Offre améliorée avec succès !');
      
      decrementCredits();
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de l\'amélioration de l\'offre.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(editableText);
    setSuccess('Texte copié dans le presse-papier !');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleExport = () => {
    if (!editableText) {
      setError('Aucune offre à exporter');
      return;
    }

    try {
      const blob = new Blob([editableText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `offre-${position || 'emploi'}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Offre exportée avec succès !');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      setError('Une erreur est survenue lors de l\'export');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleSave = async () => {
    if (!editableText) {
      setError('Aucune offre à sauvegarder');
      return;
    }

    try {
      const postToSave = {
        id: `post-${Date.now()}`,
        title: position || 'Sans titre',
        company: companyName,
        content: editableText,
        date: new Date().toISOString(),
        formData: {
          companyName,
          position,
          location,
          experience,
          skills,
          contractType,
          tone,
          jobLink,
          companyValues
        }
      };

      await saveJobPost(postToSave);
      setSuccess('Votre offre a été sauvegardée !');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.message || 'Une erreur est survenue lors de la sauvegarde');
      setTimeout(() => setError(null), 3000);
    }
  };

  const selectedToneOption = toneOptions.find(option => option.id === tone);
  const selectedContractOption = contractOptions.find(option => option.id === contractType);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
        <Sparkles className="w-6 h-6 text-indigo-600 mr-2" />
        Pimper une offre
      </h2>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 text-green-600 p-4 rounded-md mb-6 flex items-center">
          <Check className="w-5 h-5 mr-2" />
          <p>{success}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left column - Input form */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Informations sur l'offre</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texte de l'offre originale *
              </label>
              <textarea
                value={originalText}
                onChange={(e) => setOriginalText(e.target.value)}
                placeholder="Collez ici le texte de votre offre d'emploi actuelle..."
                className="w-full h-[200px] p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'entreprise
              </label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: TechInnovate"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Site web de l'entreprise
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Globe className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="url"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: https://www.entreprise.com"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Le site web sera utilisé pour s'inspirer du ton et des valeurs de l'entreprise
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Poste
              </label>
              <input
                type="text"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: Développeur Full-Stack"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lieu
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: Paris, France (Remote possible)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expérience requise
              </label>
              <input
                type="text"
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: 3+ ans d'expérience"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compétences
              </label>
              <textarea
                value={skills}
                onChange={(e) => setSkills(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: React, Node.js, SQL, NoSQL"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de contrat
              </label>
              <button
                type="button"
                onClick={() => setIsContractModalOpen(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {selectedContractOption ? (
                  <span className="flex items-center">
                    <span className="mr-2">{selectedContractOption.emoji}</span>
                    <span>{selectedContractOption.title}</span>
                  </span>
                ) : (
                  <span>Sélectionner un type de contrat</span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ton de l'offre
              </label>
              <button
                type="button"
                onClick={() => setIsToneModalOpen(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-left flex items-center justify-between focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {selectedToneOption ? (
                  <span className="flex items-center">
                    <span className="mr-2">{selectedToneOption.emoji}</span>
                    <span>{selectedToneOption.title}</span>
                  </span>
                ) : (
                  <span>Sélectionner un ton</span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lien pour postuler
              </label>
              <input
                type="url"
                value={jobLink}
                onChange={(e) => setJobLink(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Ex: https://entreprise.com/carrieres"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valeurs de l'entreprise (max 5)
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Ex: Innovation"
                  disabled={companyValues.length >= 5}
                />
                <button
                  onClick={handleAddValue}
                  disabled={!newValue.trim() || companyValues.length >= 5}
                  className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
              
              {companyValues.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {companyValues.map((value, index) => (
                    <div key={index} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full flex items-center">
                      <span>{value}</span>
                      <button
                        onClick={() => handleRemoveValue(index)}
                        className="ml-2 text-indigo-500 hover:text-indigo-700"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-4">
              <button
                onClick={handlePimperOffre}
                disabled={isProcessing || !originalText.trim()}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    <span>Pimper cette offre</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Right column - Output */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Offre améliorée</h3>
            
            {pimpedText && (
              <div className="flex space-x-2">
                <button
                  onClick={handlePimperOffre}
                  disabled={isProcessing}
                  className="flex items-center space-x-1 text-indigo-600 hover:text-indigo-800 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isProcessing ? 'animate-spin' : ''}`} />
                  <span>Régénérer</span>
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  <span>Copier</span>
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>Sauvegarder</span>
                </button>
                <button
                  onClick={handleExport}
                  className="flex items-center space-x-1 text-gray-600 hover:text-indigo-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Exporter</span>
                </button>
              </div>
            )}
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-md overflow-hidden">
            {isProcessing ? (
              <div className="flex items-center justify-center h-[600px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Amélioration en cours...</p>
                </div>
              </div>
            ) : pimpedText ? (
              <div className="p-6">
                <TextEditor
                  value={editableText}
                  onChange={setEditableText}
                  height="600px"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[600px]">
                <div className="text-center text-gray-500">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Remplissez le formulaire et cliquez sur "Pimper cette offre"</p>
                  <p className="text-sm mt-2">L'offre améliorée apparaîtra ici</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal for tone selection */}
      {isToneModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={toneModalRef}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Ton de l'offre</h3>
              <button 
                onClick={() => setIsToneModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {toneOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setTone(option.id as PostTone);
                    setIsToneModalOpen(false);
                  }}
                  className={`p-4 border rounded-lg text-left hover:bg-gray-50 transition-colors ${
                    tone === option.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="text-2xl mr-3">{option.emoji}</div>
                    <div>
                      <div className="font-medium">{option.title}</div>
                      <div className="text-sm text-gray-500">{option.description}</div>
                    </div>
                    {tone === option.id && (
                      <Check className="ml-auto text-indigo-600 w-5 h-5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal for contract type selection */}
      {isContractModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={contractModalRef}
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Type de contrat</h3>
              <button 
                onClick={() => setIsContractModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {contractOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setContractType(option.id as ContractType);
                    setIsContractModalOpen(false);
                  }}
                  className={`p-4 border rounded-lg text-left hover:bg-gray-50 transition-colors ${
                    contractType === option.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="text-2xl mr-3">{option.emoji}</div>
                    <div>
                      <div className="font-medium">{option.title}</div>
                      <div className="text-sm text-gray-500">{option.description}</div>
                    </div>
                    {contractType === option.id && (
                      <Check className="ml-auto text-indigo-600 w-5 h-5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const toneOptions = [
  { id: 'Formelle', title: 'Formelle', description: 'Ton professionnel et conventionnel', emoji: '👔' },
  { id: 'Décontractée', title: 'Décontractée', description: 'Ton amical et accessible', emoji: '😊' },
  { id: 'Provocante', title: 'Provocante', description: 'Ton audacieux qui attire l\'attention', emoji: '🔥' },
  { id: 'Inspirante', title: 'Inspirante', description: 'Ton motivant et enthousiaste', emoji: '✨' }
];

const contractOptions = [
  { id: 'CDI', title: 'CDI', description: 'Contrat à durée indéterminée', emoji: '📝' },
  { id: 'CDD', title: 'CDD', description: 'Contrat à durée déterminée', emoji: '⏱️' },
  { id: 'Freelance', title: 'Freelance', description: 'Mission indépendante', emoji: '🚀' },
  { id: 'Stage', title: 'Stage', description: 'Formation professionnelle', emoji: '🎓' },
  { id: 'Alternance', title: 'Alternance', description: 'Formation en alternance', emoji: '🔄' }
];

export default PimperPage;